import { v4 as uuidv4 } from 'uuid';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { getAllCompositeSegments } from '@goldcast/player-config/src/utils/clipUtils';

/**
 * Deep-clones every composite segment of a source clip so it can be spliced into another clip's
 * timeline as a composite — keeping scenes, video feeds, speaker labels and layoutChanges intact
 * rather than flattening the segment down to a single video source.
 *
 * Only composites are copied: intro/outro are VIDEO segments and are excluded by construction.
 * The imported segments are a point-in-time snapshot — later edits to the source clip do not
 * propagate. Translations are dropped so the importing clip's own language stays authoritative.
 */
export function getClipCompositeSegments(clip: Clip): CompositeSegment[] {
  const compositeSegments = getAllCompositeSegments(clip);

  return compositeSegments.map(segment => {
    const cloned = structuredClone(segment);
    // Fresh id so the segment cannot collide with one already on the target timeline.
    cloned.id = uuidv4();

    // A clip with no splits carries its editing state on asset_metadata rather than on the
    // segment, so synthesize the metadata the target timeline's splits path requires.
    if (!cloned.segment_metadata) {
      const meta = clip.asset_metadata;
      // start/end can both be 0 for an unedited clip, but filterVerySmallComposites drops any
      // composite shorter than 0.5s — fall back to duration so the import is never discarded.
      const clipDuration = meta.duration ?? 0;
      const segStart = meta.start ?? 0;
      const segEnd = meta.end && meta.end > segStart ? meta.end : clipDuration;
      cloned.segment_metadata = {
        start: segStart,
        end: segEnd,
        deletes: meta.deletes ?? {},
        edits: meta.edits ?? {},
        merges: meta.merges ?? [],
        excludes: meta.excludes ?? [],
        captions: meta.captions ?? [],
        fitMode: meta.fit,
        scenes: structuredClone(meta.scenes ?? cloned.scenes ?? []),
        duration: clipDuration,
        maxVideoDuration: meta.maxVideoDuration ?? segEnd,
        captions_lang: meta.original_lang
      };
    }

    // Ignore translations from the source clip — captions are kept in the original language only.
    cloned.segment_metadata.captions_lang = clip.asset_metadata.original_lang;

    return cloned;
  });
}
